import { StyledIconBase } from "@styled-icons/styled-icon";
import { Add, AddImage, Delete } from "assets/icons/Common";
import { Button, Col, IconButton, IconButtonInverted, Row } from "components/primitives";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { FC, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import DatePicker, { DateObject } from "react-multi-date-picker";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";

import { ScheduleItemData } from "./interfaces";

interface Props {
  activity?: ScheduleItemData;
  hasClosed?: () => void;
  onSubmit?: (newActivity: ScheduleItemData) => void;
  isSavedActivity: boolean;
}

const Colors = ["#80A0B6", "#7FDFDB", "#B4ADE9", "#FBBAD2", "#FFB4A0", "#FEE9A5"];

const BuildDefaultActivity = (): ScheduleItemData => ({
  id: uuidv4(),
  activity: "",
  color: Colors[0],
  time: "",
  link: "",
});

export const EditActivityModal: FC<Props> = ({
  activity: currentActivity,
  hasClosed,
  onSubmit,
  isSavedActivity,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [activity, setActivity] = useState(currentActivity || BuildDefaultActivity());
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const timeValue = activity.time ? new Date(activity.time) : undefined;

  const action = currentActivity ? "Edit" : "Add";

  const updateActivity = (newActivity: Partial<ScheduleItemData>) => {
    setActivity({
      ...activity,
      ...newActivity,
    });
  };

  const onClose = () => {
    setIsClosing(true);
  };

  const onChangeName = (name: string) => {
    updateActivity({ activity: name });
  };

  const onChangeTime = (time: Date | undefined) => {
    updateActivity({ time: time?.toISOString() });
  };

  const onChangeLink = (link: string) => {
    updateActivity({ link });
  };

  const onChangeColor = (color: string) => {
    updateActivity({ color });
  };

  const onImageSelected = (emojiData: EmojiClickData) => {
    setShowEmojiPicker(false);
    updateActivity({ image: emojiData.emoji });
  };

  const onSubmitModal = () => {
    if (onSubmit) {
      onSubmit(activity);
    }
    onClose();
  };

  const isValidUrl = (urlString: string) => {
    var pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-z\\d_]*)?$",
      "i",
    ); // fragment locator
    return !!pattern.test(urlString);
  };

  const isModalValid = () => {
    return (
      activity.activity.trim() !== "" &&
      (!activity.link || (activity.link.trim() && isValidUrl(activity.link)))
    );
  };

  return (
    <Modal show={!isClosing} onHide={onClose} size="lg" onExited={hasClosed} centered>
      <Modal.Header>
        <HeaderRow>
          <Add />
          {action} an Activity
        </HeaderRow>
      </Modal.Header>
      <Modal.Body>
        <BodyCol>
          <Form.Group>
            <NameLabel isInvalid={activity.activity.length === 20}>
              Name <small>(Max characters: 20)</small>
            </NameLabel>
            <Form.Control
              type="text"
              maxLength={20}
              value={activity.activity}
              onChange={(e) => onChangeName(e.target.value)}
            />
          </Form.Group>
          {!isSavedActivity && (
            <>
              <TimePickerCol>
                <Form.Label>
                  Select time <small>(optional)</small>
                </Form.Label>
                <DatePicker
                  disableDayPicker
                  value={timeValue}
                  onChange={(e: DateObject) => {
                    onChangeTime(e?.toDate());
                  }}
                  format="HH:mm"
                  plugins={[<TimePicker mStep={5} hideSeconds />]}
                />
              </TimePickerCol>
              <Form.Group>
                <Form.Label>
                  Add external link <small>(optional)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={activity.link}
                  onChange={(e) => onChangeLink(e.target.value)}
                />
                {activity.link?.trim() && !isValidUrl(activity.link) && (
                  <small style={{ color: "var(--danger)" }}>Please enter a valid link.</small>
                )}
              </Form.Group>
            </>
          )}
          <LowerRow>
            <Col>
              <p>Choose a colour</p>
              <Row style={{ gap: "5px" }}>
                {Colors.map((color) => (
                  <SelectableColor
                    key={color}
                    isHighlighted={color === activity.color}
                    color={color}
                    onClick={() => onChangeColor(color)}
                  />
                ))}
              </Row>
            </Col>
            <Col style={{ position: "relative" }}>
              <p>Image</p>
              {activity.image ? (
                <Row style={{ height: "45px", alignItems: "center" }}>
                  <Button
                    style={{ boxShadow: "none", width: "45px", height: "45px", fontSize: "15pt" }}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    {activity.image}
                  </Button>
                  <IconButtonInverted
                    accentColor="var(--danger)"
                    Icon={Delete}
                    onClick={() => updateActivity({ image: "" })}
                    style={{ height: "70%", padding: "8px" }}
                    withShadow={false}
                  />
                </Row>
              ) : (
                <EmojiButton
                  withShadow={false}
                  Icon={AddImage}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                />
              )}
              <EmojiPickerContainer show={showEmojiPicker}>
                <EmojiPicker
                  skinTonesDisabled={true}
                  onEmojiClick={onImageSelected}
                  emojiStyle={EmojiStyle.NATIVE}
                />
              </EmojiPickerContainer>
            </Col>
          </LowerRow>
          <Footer>
            <FooterButton color="var(--danger)" onClick={onClose}>
              Cancel
            </FooterButton>
            <FooterButton color="var(--primary)" onClick={onSubmitModal} disabled={!isModalValid()}>
              {action} Activity
            </FooterButton>
          </Footer>
        </BodyCol>
      </Modal.Body>
    </Modal>
  );
};

const HeaderRow = styled(Row)`
  color: var(--secondary);
  align-items: center;
  gap: 10px;
  height: 30px;
  font-size: 16pt;
  font-weight: 600;

  ${StyledIconBase} {
    height: 100%;
  }
`;

const BodyCol = styled(Col)``;

const LowerRow = styled(Row)`
  margin-bottom: 1rem;
  gap: 50px;
`;

const EmojiButton = styled(IconButton)`
  width: 45px;
  height: 45px;
`;

const SelectableColor = styled(Button)<{ color: string; isHighlighted: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 5px;
  background-color: ${(props) => props.color};
  box-shadow: none;
  border: ${(props) => (props.isHighlighted ? "3px solid #417192" : "none")};
`;

const Footer = styled(Row)`
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const FooterButton = styled(Button)<{ color: string }>`
  background-color: ${(props) => props.color};
  box-shadow: none;
  color: black;
  width: 20%;
`;

const EmojiPickerContainer = styled.div<{ show: boolean }>`
  display: ${(props) => (props.show ? "initial" : "none")};
  position: absolute;
  left: 50px;
  top: 0;
  transform: translate(0, -50%);
  z-index: 10;
`;

const TimePickerCol = styled(Col)`
  .rmdp-time-picker {
    input {
      width: 40px;
    }
  }

  .rmdp-input {
    height: 38px;
    padding: 0.375rem 0.75rem;
    color: #495057;
    margin-bottom: 1rem;
  }
`;

const NameLabel = styled(Form.Label)<{ isInvalid: boolean }>`
  color: ${(props) => (props.isInvalid ? "var(--orange)" : "var(--secondary)")};
`;
